import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
    createImportedVehicleApi,
    createVehicleApi,
    deleteImportedVehicleApi,
    deleteVehicleApi,
    getVehicleDetailsApi,
    getVehicleListApi,
    updateImportedVehicleApi,
    updateVehicleApi,
} from './vehicle.api';

// Query keys for vehicle operations
export const vehicleKeys = {
    all: ['vehicles'],
    lists: () => [...vehicleKeys.all, 'list'],
    list: (filters) => [...vehicleKeys.lists(), { filters }],
    details: () => [...vehicleKeys.all, 'detail'],
    detail: (id) => [...vehicleKeys.details(), id],
};

/**
 * Hook to get vehicle list for a customer
 * @param {object} params - Query parameters
 * @param {string} params.customerId - Customer ID
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {object} options - TanStack Query options
 * @returns {object} - Query result with data, loading, error, etc.
 */
export const useGetVehicleList = (params = {}, options = {}) => {
    return useQuery({
        queryKey: vehicleKeys.list(params),
        queryFn: () => getVehicleListApi(params),
        enabled: !!params.customerId,
        ...options,
    });
};

/**
 * Hook to get vehicle details by ID
 * @param {string} vehicleId - Vehicle ID
 * @param {object} options - TanStack Query options
 * @returns {object} - Query result with data, loading, error, etc.
 */
export const useGetVehicleDetails = (vehicleId, options = {}) => {
    return useQuery({
        queryKey: vehicleKeys.detail(vehicleId),
        queryFn: () => getVehicleDetailsApi(vehicleId),
        enabled: !!vehicleId,
        ...options,
    });
};

/**
 * Hook to create a new vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useCreateVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createVehicleApi,
        onSuccess: (data, variables) => {
            // Invalidate and refetch vehicle lists for the customer
            toast.success(data?.message);
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error, variables) => {
            console.log('🚀 ~ useCreateVehicle ~ error:', error);
            toast.error(error?.response?.data?.message || 'Failed to create vehicle');
            // Call custom onError if provided
            if (options.onError) {
                options.onError(error, variables);
            }
        },
        ...options,
    });
};

/**
 * Hook to create a new vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useCreateImportedVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ vehicleId, vehicleData }) => createImportedVehicleApi(vehicleId, vehicleData),
        onSuccess: (data, variables) => {
            // Invalidate and refetch vehicle lists for the customer
            toast.success(data?.message);

            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error, variables) => {
            console.log('🚀 ~ useCreateImportedVehicle ~ error:', error);
            toast.error(error?.response?.data?.message || 'Failed to create vehicle from imported data');
            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(error, variables);
            }
        },
        ...options,
    });
};

/**
 * Hook to update an imported vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useUpdateImportedVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ vehicleId, vehicleData }) => updateImportedVehicleApi(vehicleId, vehicleData),
        onSuccess: (data, variables) => {
            toast.success(data?.message);
            // Invalidate and refetch vehicle lists
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Invalidate specific vehicle details
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.detail(variables.vehicleId),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error, variables) => {
            console.log('🚀 ~ useUpdateImportedVehicle ~ error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update imported vehicle');
            // Call user's onError if provided
            if (options.onError) {
                options.onError(error, variables);
            }
        },
        ...options,
    });
};

/**
 * Hook to delete an imported vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useDeleteImportedVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteImportedVehicleApi,
        onSuccess: (data, variables) => {
            // Invalidate and refetch vehicle lists
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Remove specific vehicle details from cache
            queryClient.removeQueries({
                queryKey: vehicleKeys.detail(variables),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error, variables) => {
            console.log('🚀 ~ useDeleteImportedVehicle ~ error:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete imported vehicle');
            // Call user's onError if provided
            if (options.onError) {
                options.onError(error, variables);
            }
        },
        ...options,
    });
};

/**
 * Hook to update an existing vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useUpdateVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ vehicleId, vehicleData }) => updateVehicleApi(vehicleId, vehicleData),
        onSuccess: (data, variables) => {
            toast.success(data?.message);
            // Invalidate and refetch vehicle lists
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Invalidate specific vehicle details
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.detail(variables.vehicleId),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        onError: (error, variables) => {
            console.log('🚀 ~ useUpdateVehicle ~ error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update vehicle');
            // Call user's onError if provided
            if (options.onError) {
                options.onError(error, variables);
            }
        },
        ...options,
    });
};

/**
 * Hook to delete a vehicle
 * @param {object} options - Mutation options
 * @returns {object} - Mutation result with mutate, mutateAsync, etc.
 */
export const useDeleteVehicle = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteVehicleApi,
        onSuccess: (data, variables) => {
            // Invalidate and refetch vehicle lists
            queryClient.invalidateQueries({
                queryKey: vehicleKeys.lists(),
            });

            // Remove specific vehicle details from cache
            queryClient.removeQueries({
                queryKey: vehicleKeys.detail(variables),
            });

            // Call custom onSuccess if provided
            if (options.onSuccess) {
                options.onSuccess(data, variables);
            }
        },
        ...options,
    });
};
